import { useQuery } from '@tanstack/react-query';
import { CacheConfig } from './consts';
import authFetch from './fetch';
import { getQueryParams, makeRequest } from './utils';

const TRUCK_PATH = 'api/Trucks';
const TRUCK_PERMIT_RULES_PATH = 'api/TruckPermitRules';

const routes = {
  truckFilesPathByTruckId: (truckId) => `/api/Trucks/${truckId}/Files`,
  truckFilePath: (truckId, fileId) => `/api/Trucks/${truckId}/Files/${fileId}`,
};

export const getTrucksPaginated = async (params) => makeRequest(authFetch.get(
      `${TRUCK_PATH}/Grid${getQueryParams(params)}`,
));

export const getTrucks = async (params) => makeRequest(
  authFetch.get(`${TRUCK_PATH}${getQueryParams(params)}`),
);

export const postTruck = (truck) => makeRequest(authFetch.put(TRUCK_PATH, truck));

export const getTruck = async (truckId) => {
  if (!truckId === null || truckId === undefined) {
    return Promise.resolve(null);
  }

  try {
    const response = await authFetch.get(`${TRUCK_PATH}/${truckId}`);
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const getTruckFiles = (truckId, params) => makeRequest(
  authFetch.get(`${routes.truckFilesPathByTruckId(truckId)}${getQueryParams(params)}`),
);

export const postTruckFiles = (truckId, files) => makeRequest(
  authFetch.post(routes.truckFilesPathByTruckId(truckId), files),
);

export const deleteTruckFile = (truckId, fileId) => makeRequest(
  authFetch.delete(routes.truckFilePath(truckId, fileId)),
);

export const putTruckFile = (truckId, fileId, file) => makeRequest(
  authFetch.put(routes.truckFilePath(truckId, fileId), file),
);

export const getTruckFileTypes = () => authFetch
  .get(`${TRUCK_PATH}/FileTypes`)
  .then(({ data }) => data)
  .catch((error) => {
    console.error(error);
    throw error;
  });

// Truck Status

export const sequenceTruckStatuses = async (statuses) => {
  try {
    const response = await authFetch.post(`${TRUCK_PATH}/SequenceTruckStatuses`, statuses);
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const getTruckStatuses = () => makeRequest(authFetch.get(`${TRUCK_PATH}/Statuses/Types`));
export const useGetTruckStatuses = () => useQuery(['truckStatuses'], getTruckStatuses, CacheConfig.forever);

export const getTruckStatusesHistory = (truckId, params) => makeRequest(
  authFetch.get(`${TRUCK_PATH}/${truckId}/statuses${getQueryParams(params)}`),
);

export const upsertTruckStatuses = (truckId, data) => makeRequest(
  authFetch.put(`${TRUCK_PATH}/${truckId}/statuses`, data),
);

export const deleteTruckStatus = (truckId, statusId) => makeRequest(
  authFetch.delete(`${TRUCK_PATH}/${truckId}/statuses/${statusId}`),
);

export const getTruckInServicePrevalidation = (truckId) => makeRequest(
  authFetch.get(`${TRUCK_PATH}/${truckId}/PrevalidateInService`),
);

export const getTruckTypes = () => makeRequest(authFetch.get(`${TRUCK_PATH}/Types`));
export const useGetTruckTypes = () => useQuery(['truckTypes'], getTruckTypes, CacheConfig.forever);

export const getTruckNotes = (truckId, params) => makeRequest(
  authFetch.get(`${TRUCK_PATH}/${truckId}/notes${getQueryParams(params)}`),
);

export const saveTruckNote = (truckId, note) => makeRequest(
  authFetch.post(`${TRUCK_PATH}/${truckId}/notes`, note),
);

export const pinTruckNote = (truckId, noteId, pinState) => makeRequest(
  authFetch.patch(`${TRUCK_PATH}/${truckId}/notes/${noteId}/pin`, pinState),
);

export const getTireTypes = async () => {
  try {
    // HACK - You may want to redo this properly at some point - since I just made sure the interface worked
    const response = await authFetch.get('/api/TireSizes');
    return Promise.resolve(response.data.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const getLicensePlateTypes = async () => {
  try {
    const response = await authFetch.get(`${TRUCK_PATH}/LicensePlates/Types`);
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const getTruckLoans = (params) => authFetch
  .get(`${TRUCK_PATH}/Loans${getQueryParams(params)}`)
  .catch((error) => {
    console.error(error);
    throw error;
  });

export const postTruckLoan = async (loan) => {
  try {
    const response = await authFetch.post(`${TRUCK_PATH}/Loans`, loan);
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const getLoanStatuses = async () => {
  try {
    const response = await authFetch.get(`${TRUCK_PATH}/LoanStatuses`);
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const getOwnerTypes = () => makeRequest(authFetch.get(`${TRUCK_PATH}/OwnerTypes`));
export const useGetOwnerTypes = () => useQuery(['truckOwnerTypes'], getOwnerTypes, CacheConfig.forever);

export const getLoanHistory = async (id) => {
  try {
    const response = await authFetch.get(`${TRUCK_PATH}/LoanHistory?TruckLoanId=${id}`);
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const getTruckAudits = (id, params) => makeRequest(
  authFetch.get(`${TRUCK_PATH}/${id}/Audits${getQueryParams(params)}`),
);

export const getTruckAuditProperties = (id) => makeRequest(authFetch.get(`${TRUCK_PATH}/${id}/Audits/Properties`));

export const getTruckAuditUsers = (id) => makeRequest(authFetch.get(`${TRUCK_PATH}/${id}/Audits/Users`));

export const getTruckPermitRulesUrl = (params) => `${TRUCK_PERMIT_RULES_PATH}${getQueryParams(params)}`;
export const getTruckPermitRules = (params) => makeRequest(authFetch.get(getTruckPermitRulesUrl(params)));
export const useGetTruckPermitRules = (params) => useQuery([getTruckPermitRulesUrl(params)], getTruckPermitRules);
export const lookupTruckPermitRules = (params) => makeRequest(authFetch.get(`${TRUCK_PERMIT_RULES_PATH}/lookup${getQueryParams(params)}`));
export const getTruckPermitRuleFiles = (id, params) => makeRequest(authFetch.get(`${TRUCK_PERMIT_RULES_PATH}/${id}/Files${getQueryParams(params)}`));
export const postTruckPermitRuleFiles = (id, files) => makeRequest(authFetch.post(`${TRUCK_PERMIT_RULES_PATH}/${id}/Files`, files));
export const putTruckPermitRuleFiles = (truckId, fileId, file) => makeRequest(authFetch.put(`${TRUCK_PERMIT_RULES_PATH}/${truckId}/Files/${fileId}`, file));

export const getTruckPermitRuleAudits = (id, params) => makeRequest(authFetch.get(`${TRUCK_PERMIT_RULES_PATH}/${id}/Audits${getQueryParams(params)}`));

export const getTruckPermitRuleAuditProperties = (id) => makeRequest(authFetch.get(`${TRUCK_PERMIT_RULES_PATH}/${id}/Audits/Properties`));

export const getTruckPermitRuleAuditUsers = (id) => makeRequest(authFetch.get(`${TRUCK_PERMIT_RULES_PATH}/${id}/Audits/Users`));

export const deleteTruckPermitRule = (id) => makeRequest(authFetch.delete(`${TRUCK_PERMIT_RULES_PATH}/${id}`));
export const restoreTruckPermitRule = (id) => makeRequest(authFetch.patch(`${TRUCK_PERMIT_RULES_PATH}/${id}/restore`));

export const upsertTruckPermitRuleState = (id, state) => makeRequest(authFetch.put(`${TRUCK_PERMIT_RULES_PATH}/${id}/states`, state));
export const deleteTruckPermitRuleState = (id, state) => makeRequest(authFetch.delete(`${TRUCK_PERMIT_RULES_PATH}/${id}/states/${state}`));

export const getTruckPermitRuleTypes = () => makeRequest(authFetch.get(`${TRUCK_PERMIT_RULES_PATH}/TruckPermitRuleTypes`));

export const upsertTruckPermitRule = (rule) => makeRequest(authFetch.put(`${TRUCK_PERMIT_RULES_PATH}`, rule));

export const addTruckPermitRuleFiles = (id, files) => makeRequest(authFetch.post(`${TRUCK_PERMIT_RULES_PATH}/${id}/Files`, files));

export const deleteTruckPermitRuleFile = (id, fileId) => makeRequest(authFetch.delete(`${TRUCK_PERMIT_RULES_PATH}/${id}/Files/${fileId}`));

export const getOverweightTruckPermits = (truckId, params) => makeRequest(
  authFetch.get(`${TRUCK_PATH}/${truckId}/OverweightTruckPermits${getQueryParams(params)}`),
);

export const putOverweightTruckPermit = (data) => makeRequest(
  authFetch.put(`${TRUCK_PATH}/OverweightTruckPermit`, data),
);

export const getOverweightTruckPermitStatuses = () => makeRequest(
  authFetch.get(`${TRUCK_PATH}/OverweightTruckPermit/Statuses`),
);

export const getLoanFiles = (truckLoanId, params) => makeRequest(
  authFetch.get(`${TRUCK_PATH}/Loan/${truckLoanId}/Files${getQueryParams(params)}`),
);

export const updateTruckLoanFileInfo = async (truckLoanId, fileId, info) => {
  try {
    const response = await authFetch.put(`${TRUCK_PATH}/Loan/${truckLoanId}/Files/${fileId}`, info);
    return Promise.resolve(response.data);
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const postLoanFiles = (truckLoanId, files) => makeRequest(
  authFetch.post(`${TRUCK_PATH}/Loan/${truckLoanId}/Files`, files),
);

export const deleteLoanFile = (truckLoanId, fileId) => makeRequest(
  authFetch.delete(`${TRUCK_PATH}/Loan/${truckLoanId}/Files/${fileId}`),
);

export const getLoanDocumentTypes = () => makeRequest(authFetch.get(`${TRUCK_PATH}/Loan/DocumentTypes`));

export const getTruckReassignmentReasons = async (params) => ({
  data: await makeRequest(authFetch.get(`/api/TruckReassignmentReasons${getQueryParams(params)}`)),
});
export const useGetTruckReassignmentReasons = () => useQuery(
  ['truckReassignmentReasons'],
  (params) => getTruckReassignmentReasons(params).then(({ data }) => data),
  CacheConfig.forever,
);

export const postTruckReassignmentReason = (reassignmentReason) => makeRequest(
  authFetch.put('/api/TruckReassignmentReasons', reassignmentReason),
);

export const deleteTruckReassignmentReason = (id) => makeRequest(
  authFetch.delete(`/api/TruckReassignmentReasons/${id}`),
);

export const deleteTruckAssignment = (truckId, options) => makeRequest(authFetch.delete(`${TRUCK_PATH}/${truckId}/unit`, options));

export const getTruckUnitHistory = (truckId) => makeRequest(authFetch.get(`${TRUCK_PATH}/${truckId}/unit/history`));

export const getTruckInspections = (truckId) => makeRequest(
  authFetch.get(`${TRUCK_PATH}/${truckId}/inspections`),
);

export const upsertTruckInspections = (truckId, data) => makeRequest(
  authFetch.put(`${TRUCK_PATH}/${truckId}/inspections`, data),
);

export const getTruckInspectionTypes = () => makeRequest(
  authFetch.get(`${TRUCK_PATH}/inspections/types`),
);

export const getTruckAssignments = (truckId, params) => makeRequest(
  authFetch.get(`/api/Units/drivers${getQueryParams({ ...params, PreviouslyAssignedToId: truckId })}`),
);

// Bulk Change
export const changeAllValidate = (data) => makeRequest(
  authFetch.patch(`${TRUCK_PATH}/changeall/validate`, data),
);

export const changeAllAssign = (data) => makeRequest(
  authFetch.patch(`${TRUCK_PATH}/changeall/assign`, data),
);
